import React, { useContext, useState, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorContext from 'ErrorContext';
import SessionContext from "SessionContext";
import { ProgressLoaderSimple } from '../partials/progress-loader';
import { 
  Button, 
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert, } from 'reactstrap';

// global variables used in the timer
let timeToWait = 60000; // 60 seconds
let hasSixtySecondsPassed = true;
let timeoutId = null;
let pendingXFilterTimerId = null;
let xFilterReloadingActive = false;
const RESTART_DELAY = 5000;

export default function ErrorNotifications() {
  const errors = useContext(ErrorContext);
  const session = useContext(SessionContext);

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [hasInternetConnection, setHasInternetConnection] = useState(false);
  const [hasApiConnection, setHasApiConnection] = useState(false);
  const [errorDetails, setErrorDetails] = useState("");
  const [apiHealthDetails, setApiHealthDetails] = useState("");
  const [userInfo, setUserInfo] = useState("");
  const [companyInfo, setCompanyInfo] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorIdForEmail, setErrorIdForEmail] = useState("");
  const [isXFilterReloading, setIsXFilterReloading] = useState(false);
  const errorsRef = useRef(errors);

  const clear = () => {
    errors.clearMessages();
    xFilterReloadingActive = false;
    if (pendingXFilterTimerId) {
      clearTimeout(pendingXFilterTimerId);
      pendingXFilterTimerId = null;
    }
    if (isXFilterReloading) {
      window.location.replace('/');
      return;
    }
    setShowErrorMessage(false);
    setIsXFilterReloading(false);
    startWaitTimer();
  }

  const checkInternetAccess = () => {
    const isOnline = navigator.onLine;
    if(isOnline) {
      checkApiAccess();
      return true;
    } else {
      setIsLoading(false);
      hasSixtySecondsPassed = false;
      return false;
    }
  };

  const checkApiHealth = () => {
    let isHealthy = false;

    const params = {};
    // use only the url up to the first single /, discard the rest
    const _url = window.api_url.match(/^https*:\/\/[^/]*/)[0];

    const xhrConfig = {
      base_url: _url,
    };

    session.query("actuator/health", params, xhrConfig)
      .then((res) => {
        if(res && 'components' in res) {
          setHasApiConnection(true);
          isHealthy = true;
          setApiHealthDetails(JSON.stringify(res.components));
        } else {
          setHasApiConnection(false);
          setApiHealthDetails("No response from the API");
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("actuator/health failed", err)
        setHasApiConnection(false);
        setIsLoading(false);
        setApiHealthDetails("API health check failed");
      })
      .finally(() => {
        return isHealthy;
      });
  }

  const checkApiAccess = () => {
    // check if the API is reachable
    if(hasSixtySecondsPassed) {
      hasSixtySecondsPassed = false;
      checkApiHealth();
    }
  }

  const getDisplayMessage = () => {
    if(!hasInternetConnection) {
      return <NoInternetError />;
    }
    if(hasApiConnection) {
      return (
        <UnknownError 
          errorDetails={errorDetails} 
          userInfo={userInfo} 
          companyInfo={companyInfo} 
          errorLogId={errorIdForEmail}
          apiHealthDetails={apiHealthDetails}
        />);
    }
    return <NoApiError errorDetails={errorDetails} userInfo={userInfo} companyInfo={companyInfo} apiHealthDetails={apiHealthDetails} />;
  }

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  useEffect(() => {
    if (!errors || errors.messages.length === 0) return;
    if (pendingXFilterTimerId) return;

    const hasXFilterReloadingNow = errors.messages.some(msg => msg?.statusCode === 424);
    // 424 bypasses the 60s wait timer and any existing modal — it always wins
    // Normal errors respect both guards
    const canStart = hasXFilterReloadingNow ? !xFilterReloadingActive : (!showErrorMessage && hasSixtySecondsPassed);
    if (!canStart) return;

    // suppress JS errors immediately — don't wait for the timer to fire
    if(hasXFilterReloadingNow) xFilterReloadingActive = true;

    pendingXFilterTimerId = setTimeout(() => {
      pendingXFilterTimerId = null;
      const currentMessages = errorsRef.current?.messages || [];
      const hasXFilterReloading = currentMessages.some(msg => msg?.statusCode === 424);
      xFilterReloadingActive = hasXFilterReloading;
      setIsXFilterReloading(hasXFilterReloading);
      setShowErrorMessage(true);

      if(hasXFilterReloading) {
        setIsLoading(false);
      } else {
        setIsLoading(true);
        setHasInternetConnection(checkInternetAccess());
        const firstMsg = currentMessages[0];
        if (firstMsg) {
          let _time_stamp = new Date(firstMsg.ts);
          setErrorDetails(JSON.stringify({ ...firstMsg, ts: _time_stamp.toLocaleString() }) || "");
        }
        setUserInfo(session.appState.identity.user.name || "");
        setCompanyInfo(session.appState.activeCompany.name || "");
      }
    }, RESTART_DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if(xFilterReloadingActive) return;
    if(hasInternetConnection && hasApiConnection && errors && errors.messages.length > 0) {
      let _time_stamp = new Date(errors.messages[0].ts).toISOString();
      const error_details = {
        "id": null,
        "am_user_id": session.appState.identity.user.id,
        "am_company_id": session.appState.activeCompany.id,
        "ip_address": "",
        "call_timestamp": _time_stamp,
        "error_timestamp": _time_stamp,
        "api_called": errors.messages[0].url || "",
        "payload": { payload: errors.messages[0].details, audience: session.crossfilterStore.audience },
        "request_id": errors.messages[0].requestId,
        "error_returned": "",
        "response_code": errors.messages[0].statusCode,
        "response_text": errors.messages[0].message,
        "api_health": JSON.parse(apiHealthDetails),
      };
      
      session.command("admin/create_error_log", error_details, { defaultErrorHandling: false })
        .then((res) => {
          console.log("The error was logged successfully", res);
          setErrorIdForEmail(res.id);
        })
        .catch((err) => {
          console.log("The error was not logged", err);
          setErrorIdForEmail("");
          setHasApiConnection(false);
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInternetConnection, hasApiConnection]);

  useEffect(() => {
    const handleError = (event) => {
      if(xFilterReloadingActive) { event.preventDefault(); return; }
      console.error(event.error);
      const ts = new Date().toISOString();

      let error_details = {
        id: null,
        am_user_id: session?.appState?.identity?.user?.id || null,
        am_company_id: session?.appState?.activeCompany?.id || null,
        ip_address: "",
        call_timestamp: ts,
        error_timestamp: ts,
        api_called: event.filename || window.location.href,
        payload: { message: event.message, filename: event.filename, lineno: event.lineno, colno: event.colno, audience: session.crossfilterStore.audience },
        request_id: null,
        error_returned: event.error?.stack || event.message,
        response_code: 100,
        response_text: event.message,
        api_health: {
          note: "Error Not API Related",
        },
      };

      session.command("admin/create_error_log", error_details, { defaultErrorHandling: false })
        .catch((err) => console.log("JS crash error was not logged", err));
    };

    const handleUnhandledRejection = (event) => {
      if(xFilterReloadingActive) { event.preventDefault(); return; }
      console.error(event.reason);
      const ts = new Date().toISOString();

      let error_details = {
        id: null,
        am_user_id: session?.appState?.identity?.user?.id || null,
        am_company_id: session?.appState?.activeCompany?.id || null,
        ip_address: "",
        call_timestamp: ts,
        error_timestamp: ts,
        api_called: window.location.href,
        payload: { reason: String(event.reason), audience: session.crossfilterStore.audience },
        request_id: null,
        error_returned: event.reason?.stack || String(event.reason),
        response_code: 100,
        response_text: String(event.reason),
        api_health: {
          note: "Error Not API Related",
        },
      };

      session.command("admin/create_error_log", error_details, { defaultErrorHandling: false })
        .catch((err) => console.log("Unhandled rejection was not logged", err));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if(isXFilterReloading) {
    return (
      <Modal size={"lg"} centered backdrop="static" isOpen={showErrorMessage} toggle={clear}>
        <ModalHeader>
          Data Update in Progress
        </ModalHeader>
        <ModalBody>
          {isLoading ? (<div className="m-5"><StatusLoading /></div>) : (
            <div className="m-2">
              <Alert color="warning">
                <div className="d-flex justify-content-center align-items-center">
                  <p><i className="bi bi-exclamation fs-1" /></p>
                  <p>We're currently updating our data, so audience sizes may be temporarily affected or show as 0. Please try again in a few minutes.</p>
                </div>
              </Alert>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={clear}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  } else {
    return (
      <Modal size={"lg"} centered backdrop="static" isOpen={showErrorMessage} toggle={clear}>
        <ModalHeader>
          An Unexpected Error Occurred
        </ModalHeader>
        <ModalBody>
          {isLoading ? (<div className="m-5"><StatusLoading /></div>) : getDisplayMessage()}
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={clear}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const NoInternetError = () => {
  return (
    <div className="m-3">
      <Alert color="warning">
        <h5 className="alert-heading">No Internet Connection <i className="bi bi-wifi"/></h5>
        <p>It looks like you're having trouble connecting to the internet. Please check your connection and try again. If the issue persists, please contact Customer Support for assistance.</p>
      </Alert>
    </div>
  );
}

/* when the API is not available to log the error */
const NoApiError = ({errorDetails, userInfo, companyInfo}) => {
  const currentTime = new Date().toLocaleString();
  const body = encodeURIComponent( `
  User: ${userInfo} from company ${companyInfo} has reported an unhandled error. 
    
  APIs are currently not accessible to track the error - please send the below details to AudienceMaker development.
    
  Error details: 
  ${errorDetails} 
    
  Current user local time: ${currentTime}`);

  return (
    <div className="m-2">
      <Alert color="warning">
        <div className="d-flex justify-content-center align-items-center">
          <p><i className="bi bi-exclamation-triangle fs-1 text-danger" /></p>
          <p>An unexpected error occurred.<br />
        Please send an email to <a href={`mailto:audiencemaker@speedeondata.com?subject=AudienceMaker Error Reported - API not Accessible for Logging&body=${body}`} rel="noreferrer">Customer Support</a> to report the issue or try again later.</p>
        </div>
      </Alert>
    </div>
  );
}

/* all other system errors */
const UnknownError = ({errorDetails, userInfo, companyInfo, errorLogId, apiHealthDetails}) => {
  const _response = errorDetails ? JSON.parse(errorDetails) : {};
  let details = {};
  if(_response && typeof _response.details === 'object' && _response.details !== null && !Array.isArray(_response.details)) {
    details = JSON.parse(_response.details)
  } else {
    details = {error: {reason: "", description: _response.details}};
  }
  const _formatted = `
    Message: ${_response.message}
    Status Code: ${_response.statusCode} (${details.error.reason})
    Description: ${details.error.description}
    URL: ${_response.url}
    Timestamp: ${_response.ts}
    API Health: ${apiHealthDetails}
    Request ID: ${_response.requestId}
  `;
  const _errorLogId = `${window.location.origin}/admin-error-log-details#error_id=${errorLogId}`;
  const body = encodeURIComponent( `
    User: ${userInfo} from company ${companyInfo} has reported an unhandled error. 
      
    Please see error log and report to AudienceMaker development if needed. 
      
    Error details: 
    ${_formatted}

    AudienceMaker Error Log: ${_errorLogId}
    `);
  return (
    <div className="m-2">
      <Alert color="warning">
        <div className="d-flex justify-content-center align-items-center">
          <p><i className="bi bi-exclamation-triangle fs-1 text-danger" /></p>
          <p>An unexpected error occurred.<br /> Please send an email to <a href={`mailto:audiencemaker@speedeondata.com?subject=AudienceMaker Error Reported&body=${body}`} rel="noreferrer">Customer Support</a> to report the issue or try again later.</p>
        </div>
      </Alert>
    </div>
  );
}

/*
 * set a timer until the next error message is shown
 */
const startWaitTimer = () => {
  hasSixtySecondsPassed = false;

  if (timeoutId) clearTimeout(timeoutId);

  timeoutId = setTimeout(() => {
    hasSixtySecondsPassed = true;
  }, timeToWait);
}

const StatusLoading = () => {
  return (
    <div className="d-flex justify-content-center align-items-center">
      <div><ProgressLoaderSimple /></div>
    </div>
  );
};

const RenderErrorModal = ({ resetErrorBoundary }) => {
  const handleClose = () => {
    if (window.location.pathname !== '/') window.location.replace('/');
    else resetErrorBoundary();
  };

  return (
    <Modal size="lg" centered backdrop="static" isOpen>
      <ModalHeader>
        An Unexpected Error Occurred
      </ModalHeader>
      <ModalBody>
        <div className="m-2">
          <Alert color="warning">
            <div className="d-flex justify-content-center align-items-center">
              <p><i className="bi bi-exclamation-triangle fs-1 text-danger" /></p>
              <p>
                An unexpected error occurred.
                <br />Please try again or contact Customer Support if the issue persists.
                <br />Click "Close" to return to the home page.
              </p>
            </div>
          </Alert>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className="promote" onClick={handleClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export function AppErrorBoundary({ children }) {
  const session = useContext(SessionContext);

  const handleError = (error, info) => {
    const ts = new Date().toISOString();
    session.command("admin/create_error_log", {
      id: null,
      am_user_id: session?.appState?.identity?.user?.id || null,
      am_company_id: session?.appState?.activeCompany?.id || null,
      ip_address: "",
      call_timestamp: ts,
      error_timestamp: ts,
      api_called: window.location.href,
      payload: { componentStack: info.componentStack, audience: session.crossfilterStore.audience },
      request_id: null,
      error_returned: error?.stack || String(error),
      response_code: 100,
      response_text: String(error),
      api_health: { note: "React render error" },
    }, { defaultErrorHandling: false })
      .catch((err) => console.log("React render error was not logged", err));
  };

  return (
    <ErrorBoundary onError={handleError} FallbackComponent={RenderErrorModal}>
      {children}
    </ErrorBoundary>
  );
}

